import { useTranslation } from "react-i18next";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { Button, ButtonVariant } from "@industriousoffice/react-components";

import {
  uses3dsCreditCard,
  usesACH,
  usesBacs,
  usesPad,
  usesSepa,
} from "@/utils/location";
import { formatPaymentError } from "@/utils/payment-errors";

import {
  Account,
  AccountSignature,
  InvoicingSystemTypes,
  Location,
} from "@/api/opb/opbTypes";
import { opbApi } from "@/api/opb/opbApi";

import { Add3dsCreditCardDrawer } from "./Add3dsCreditCardDrawer";
import { AddPaymentMethodDrawer } from "./AddPaymentMethodDrawer";
import { AddAchAccountDrawer } from "./AddAchAccountDrawer";

import { AddPadAccountDrawer } from "./AddPadAccountDrawer";
import { AddBacsAccountDrawer } from "./AddBacsAccountDrawer";
import { AddSepaAccountDrawer } from "./AddSepaAccountDrawer";
import { FormPaymentMethodType, ZuoraQueryParamsType } from "../types";

type AddPaymentMethodsSectionProps = {
  zuoraData: ZuoraQueryParamsType | null;
  currentAccount?: Account;
  currentAccountLocation?: Location;
  isFirstPaymentMethod: boolean;
  toast: (message: string, isError?: boolean) => void;
  setZuoraData: (data: ZuoraQueryParamsType | null) => void;
  onPadSuccess: () => void;
  onPaymentMethodAddSuccess: () => Promise<void>;
  children: (actions: {
    addCard: () => void;
    addBank: (type: string) => void;
    isLoading: boolean;
  }) => ReactNode;
};

export function AddPaymentMethodsSection({
  zuoraData,
  currentAccount,
  currentAccountLocation,
  isFirstPaymentMethod,
  toast,
  setZuoraData,
  onPadSuccess,
  onPaymentMethodAddSuccess,
  children,
}: AddPaymentMethodsSectionProps) {
  const { t } = useTranslation("paymentMethods");
  const navigate = useNavigate();
  const processedCallback = useRef<string | undefined>(undefined);
  const [signatureAttempt, setSignatureAttempt] = useState(0);
  const [searchParams] = useSearchParams();

  const [errorPaymentSignature, setErrorPaymentSignature] =
    useState<Error | null>(null);
  const [isLoadingSignatureCreation, setIsLoadingSignatureCreation] =
    useState(false);

  const [isOpenAddPaymentMethodDrawer, setIsOpenAddPaymentMethodDrawer] =
    useState<boolean>(false);
  const [isOpenAdd3dsCreditCardDrawer, setIsOpenAdd3dsCreditCardDrawer] =
    useState<boolean>(false);
  const [isOpenAddAchAccountDrawer, setIsOpenAddAchAccountDrawer] =
    useState<boolean>(false);
  const [isOpenAddPADAccountDrawer, setIsOpenAddPADAccountDrawer] =
    useState<boolean>(false);
  const [isOpenAddBacsAccountDrawer, setIsOpenAddBacsAccountDrawer] =
    useState<boolean>(false);
  const [isOpenAddSepaAccountDrawer, setIsOpenAddSepaAccountDrawer] =
    useState<boolean>(false);

  // Zuora payment method signatures
  const [creditCardSignature, setCreditCardSignature] =
    useState<AccountSignature>();
  const [threeDSCreditCardSignature, setThreeDSCreditCardSignature] =
    useState<AccountSignature>();
  const [achSignature, setAchSignature] = useState<AccountSignature>();
  const [padSignature, setPadSignature] = useState<AccountSignature>();
  const [bacsSignature, setBacsSignature] = useState<AccountSignature>();
  const [sepaSignature, setSepaSignature] = useState<AccountSignature>();

  const [isCheckingPaymentSignature, setIsCheckingPaymentSignature] =
    useState(false);

  const shouldShowACH = usesACH(currentAccountLocation);
  const shouldShowPad = usesPad(currentAccountLocation);
  const shouldShowBacs = usesBacs(currentAccountLocation);
  const shouldShowSepa = usesSepa(currentAccountLocation);
  const shouldShow3dsCreditCard = uses3dsCreditCard(currentAccountLocation);

  const onOpenAddPaymentMethodDrawer = () => {
    setIsOpenAddPaymentMethodDrawer(true);
  };
  const onCloseAddPaymentMethodDrawer = () => {
    setIsOpenAddPaymentMethodDrawer(false);
  };
  const onOpenAdd3dsCreditCardDrawer = () => {
    setIsOpenAdd3dsCreditCardDrawer(true);
  };
  const onCloseAdd3dsCreditCardDrawer = () => {
    setIsOpenAdd3dsCreditCardDrawer(false);
  };

  const onOpenAddAchAccountDrawer = () => {
    setIsOpenAddAchAccountDrawer(true);
  };
  const onCloseAddAchAccountDrawer = () => {
    setIsOpenAddAchAccountDrawer(false);
  };

  const prepareDrawer = async (
    type: string,
    apply: (signature: AccountSignature) => void,
    open: () => void
  ) => {
    if (currentAccount == null || isLoadingSignatureCreation) return;
    setIsLoadingSignatureCreation(true);
    setErrorPaymentSignature(null);
    try {
      apply(await opbApi.createAccountSignature(currentAccount._id, type));
      open();
    } catch {
      setErrorPaymentSignature(
        new Error(t("We couldn’t load the payment form. Please try again."))
      );
    } finally {
      setIsLoadingSignatureCreation(false);
    }
  };
  // Create signatures for Zuora
  useEffect(() => {
    if (
      !currentAccount ||
      currentAccount?.invoicingSystemType !== InvoicingSystemTypes.Zuora
    )
      return undefined;

    let cancelled = false;
    setIsLoadingSignatureCreation(true);
    setErrorPaymentSignature(null);

    Promise.all([
      opbApi.createAccountSignature(currentAccount._id, "3ds-credit-card"),
      opbApi.createAccountSignature(currentAccount._id, "ach"),
      opbApi.createAccountSignature(currentAccount._id, "bacs"),
      opbApi.createAccountSignature(currentAccount._id, "credit-card"),
      opbApi.createAccountSignature(currentAccount._id, "pad"),
      opbApi.createAccountSignature(currentAccount._id, "sepa"),
    ])
      .then(
        ([
          currentAccount3dsCreditCardSignature,
          currentAccountAchSignature,
          currentAccountBacsSignature,
          currentAccountCreditCardSignature,
          currentAccountPadSignature,
          currentAccountSepaSignature,
        ]) => {
          if (cancelled) return;
          setThreeDSCreditCardSignature(currentAccount3dsCreditCardSignature);
          setAchSignature(currentAccountAchSignature);
          setBacsSignature(currentAccountBacsSignature);
          setCreditCardSignature(currentAccountCreditCardSignature);
          setPadSignature(currentAccountPadSignature);
          setSepaSignature(currentAccountSepaSignature);
        }
      )
      .catch(() => {
        if (!cancelled)
          setErrorPaymentSignature(
            new Error(t("We couldn’t load the payment form. Please try again."))
          );
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSignatureCreation(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentAccount?._id, signatureAttempt]);

  const redirectToPayInvoices = (hasError: boolean = false) => {
    if (zuoraData?.field_passthrough5) {
      navigate(zuoraData.field_passthrough5, {
        replace: true,
        state: {
          ...JSON.parse(zuoraData.field_passthrough6 || "{}"),
          hasError,
        },
      });
    }
  };

  // Decrypting Zuora response
  useEffect(() => {
    if (currentAccount?.invoicingSystemType !== InvoicingSystemTypes.Zuora)
      return;

    if (
      zuoraData &&
      creditCardSignature &&
      processedCallback.current !==
        JSON.stringify([
          zuoraData.signature,
          zuoraData.success,
          zuoraData.refId,
        ])
    ) {
      processedCallback.current = JSON.stringify([
        zuoraData.signature,
        zuoraData.success,
        zuoraData.refId,
      ]);
      if (zuoraData.success === "false") {
        Sentry.captureException(new Error(zuoraData.errorMessage));
        const formattedError = formatPaymentError(
          zuoraData.errorMessage || "Unknown error"
        );
        const formattedErrorMsg = `${t(formattedError.message)}${
          formattedError.code ? formattedError.code : ""
        }`;
        toast(formattedErrorMsg, true);
        setZuoraData(null);
        redirectToPayInvoices(true);
      } else {
        setIsCheckingPaymentSignature(true);

        let accountSignature: AccountSignature | undefined;
        switch (zuoraData.field_passthrough2) {
          case FormPaymentMethodType.ACH:
            accountSignature = achSignature;
            break;
          case FormPaymentMethodType.PAD:
            accountSignature = padSignature;
            break;
          case FormPaymentMethodType.BACS:
            accountSignature = bacsSignature;
            break;
          case FormPaymentMethodType.SEPA:
            accountSignature = sepaSignature;
            break;
          case FormPaymentMethodType.CREDIT_CARD:
            if (uses3dsCreditCard(currentAccountLocation)) {
              accountSignature = threeDSCreditCardSignature;
            } else {
              accountSignature = creditCardSignature;
            }
            break;
          default:
            accountSignature = creditCardSignature;
            break;
        }
        const publicKey = accountSignature?.publicKey || "";

        opbApi
          .decryptPaymentSignature(zuoraData.signature, publicKey)
          .then(async () => {
            // if the field "set as default payment method" was checked in the zuora form (we put it in the passthrough3 field)
            // update the default payment method in the user account
            if (zuoraData.field_passthrough3 === "true") {
              await opbApi.updateUserAccount({
                accountId: zuoraData.field_passthrough1,
                defaultPaymentMethodId: zuoraData.refId,
              });
            }
            // the field "field_passthrough4" is used to pass the nickname of the payment method
            // so that we can set it after the payment method is created
            // (it does not seem that sending it with the rest of the fields in the payment page form works)
            if (zuoraData.field_passthrough4?.length > 0) {
              const nickname = zuoraData.field_passthrough4;
              switch (zuoraData.field_passthrough2) {
                case FormPaymentMethodType.ACH:
                  await opbApi.updateACH(zuoraData.refId, { nickname });
                  break;
                case FormPaymentMethodType.PAD:
                  await opbApi.updatePad(zuoraData.refId, { nickname });
                  break;
                case FormPaymentMethodType.BACS:
                  await opbApi.updateBacs(zuoraData.refId, { nickname });
                  break;
                case FormPaymentMethodType.SEPA:
                  await opbApi.updateSepa(zuoraData.refId, { nickname });
                  break;
                case FormPaymentMethodType.CREDIT_CARD:
                default:
                  await opbApi.updateCreditCard(zuoraData.refId, { nickname });
                  break;
              }
            }

            redirectToPayInvoices();
            await onPaymentMethodAddSuccess();
            toast(t("Your payment method was successfully added."));
          })
          .then(() => {
            if (zuoraData.field_passthrough2 === FormPaymentMethodType.PAD) {
              onPadSuccess();
            }
          })
          .catch(async (e) => {
            setErrorPaymentSignature(
              new Error(
                t(
                  "We couldn’t confirm the new payment method or save all preferences. Review your settings before trying again."
                )
              )
            );
            try {
              await onPaymentMethodAddSuccess();
            } catch (refreshError) {
              Sentry.captureException(refreshError);
            }
            Sentry.captureException(e);
          })
          .finally(() => {
            setIsCheckingPaymentSignature(false);
            setZuoraData(null);
          });
      }
    }
  }, [zuoraData, creditCardSignature, achSignature, currentAccount]);

  const updateDefaultPaymentMethod = async (accountId: string) => {
    const setupIntent = await opbApi.retrieveSetupIntent({
      accountId,
      setupIntentId: searchParams.get("setup_intent")!,
    });

    const paymentMethodId = setupIntent.payment_method;
    if (paymentMethodId) {
      opbApi
        .updateUserAccount({
          accountId,
          defaultPaymentMethodId: paymentMethodId,
        })
        .then(() => {
          onPaymentMethodAddSuccess();
        })
        .catch((err) => {
          Sentry.captureException(err);
          toast(
            t(
              "There was a problem setting your default payment method. Please contact support."
            ),
            true
          );
        });
    }
  };

  useEffect(() => {
    if (
      currentAccount?.invoicingSystemType !== InvoicingSystemTypes.StripeBilling
    ) {
      return;
    }

    if (searchParams.get("setup_intent")) {
      if (searchParams.get("redirect_status") === "succeeded") {
        // if the "isDefault" query param is set to true, we need to update the default payment method in the user account
        const isDefault = searchParams.get("isDefault") === "true";
        if (isDefault) {
          updateDefaultPaymentMethod(currentAccount._id);
        }

        toast(t("Your payment method was successfully added."));
      } else {
        toast(
          t(
            "There was a problem processing your payment method. Please verify that your billing details are correct. If this problem persists, please contact us."
          ),
          true
        );
      }
      navigate(`/payment-methods/${currentAccount?._id}`, { replace: true });
    }
  }, [currentAccount, searchParams]);

  return (
    <>
      {errorPaymentSignature != null && (
        <p role="alert" className="payment-error payment-notice">
          {errorPaymentSignature.message}
        </p>
      )}
      {errorPaymentSignature != null && (
        <Button
          type="button"
          variant={ButtonVariant.Outline}
          onClick={() => setSignatureAttempt((value) => value + 1)}
        >
          {t("Try again")}
        </Button>
      )}
      {children({
        addCard: () => {
          if (shouldShow3dsCreditCard) onOpenAdd3dsCreditCardDrawer();
          else
            prepareDrawer(
              "credit-card",
              setCreditCardSignature,
              onOpenAddPaymentMethodDrawer
            );
        },
        addBank: (type) => {
          if (type === "ach" && shouldShowACH)
            prepareDrawer("ach", setAchSignature, onOpenAddAchAccountDrawer);
          if (type === "PAD" && shouldShowPad)
            prepareDrawer("pad", setPadSignature, () =>
              setIsOpenAddPADAccountDrawer(true)
            );
          if (type === "Bacs" && shouldShowBacs)
            prepareDrawer("bacs", setBacsSignature, () =>
              setIsOpenAddBacsAccountDrawer(true)
            );
          if (type === "SEPA" && shouldShowSepa)
            prepareDrawer("sepa", setSepaSignature, () =>
              setIsOpenAddSepaAccountDrawer(true)
            );
        },
        isLoading:
          isLoadingSignatureCreation ||
          isCheckingPaymentSignature ||
          errorPaymentSignature != null,
      })}
      {/* Credit Card - Zuora or Stripe supported */}
      <AddPaymentMethodDrawer
        isOpen={isOpenAddPaymentMethodDrawer}
        onClose={onCloseAddPaymentMethodDrawer}
        accountSignature={creditCardSignature}
        account={currentAccount}
        accountLocation={currentAccountLocation}
        isFirstPaymentMethod={isFirstPaymentMethod}
      />
      <Add3dsCreditCardDrawer
        isOpen={isOpenAdd3dsCreditCardDrawer}
        onClose={onCloseAdd3dsCreditCardDrawer}
        accountSignature={threeDSCreditCardSignature}
        accountLocation={currentAccountLocation}
        account={currentAccount}
        isFirstPaymentMethod={isFirstPaymentMethod}
      />
      {/* ACH - Zuora or Stripe supported */}
      <AddAchAccountDrawer
        isOpen={isOpenAddAchAccountDrawer}
        onClose={onCloseAddAchAccountDrawer}
        accountSignature={achSignature}
        account={currentAccount}
        accountLocation={currentAccountLocation}
        isFirstPaymentMethod={isFirstPaymentMethod}
      />
      <AddPadAccountDrawer
        isOpen={isOpenAddPADAccountDrawer}
        onClose={() => setIsOpenAddPADAccountDrawer(false)}
        accountSignature={padSignature}
        account={currentAccount}
        isFirstPaymentMethod={isFirstPaymentMethod}
        accountLocation={currentAccountLocation}
      />
      <AddBacsAccountDrawer
        isOpen={isOpenAddBacsAccountDrawer}
        onClose={() => setIsOpenAddBacsAccountDrawer(false)}
        accountSignature={bacsSignature}
        account={currentAccount}
        isFirstPaymentMethod={isFirstPaymentMethod}
        accountLocation={currentAccountLocation}
      />
      <AddSepaAccountDrawer
        isOpen={isOpenAddSepaAccountDrawer}
        onClose={() => setIsOpenAddSepaAccountDrawer(false)}
        accountSignature={sepaSignature}
        account={currentAccount}
        isFirstPaymentMethod={isFirstPaymentMethod}
        accountLocation={currentAccountLocation}
      />
    </>
  );
}
